import Profile from '../../components/Settings/Profile';
import CreditsUsage from '../../components/Settings/CreditsUsage';
import LoginActivity from '../../components/Settings/LoginActivity';
import PaymentsInvoices from '../../components/Settings/PaymentsInvoices';
import Members from '../../components/Settings/Members';
import { FEATURE_KEYS, isFeatureHidden } from './featureAccess';

/**
 * ROAR-4941 (ROAR-4899 §4 decision 3) — single source of truth for the Settings modal's submenus.
 *
 * Both the left rail and the right pane are driven from here, mirroring
 * `utils/constants/assetTypeRegistry.js`. Adding or enabling a submenu must be a ONE-ENTRY change
 * in this file plus the pane component itself — no component may hard-code a submenu list or
 * branch on `key`.
 *
 * Per-entry fields:
 *  - key        stable identifier; must equal the map key
 *  - label      rail label, and what "Search settings" matches against
 *  - group      which rail heading it sits under; must be a member of SETTINGS_GROUP_ORDER
 *  - iconName   base name of the rail icon asset pair in `public/images/`; the rail resolves it to
 *               `<iconName>Active.svg` when the entry's pane is showing and `<iconName>Inactive.svg`
 *               otherwise (the two files differ only in stroke colour). Null until the design hands
 *               over that pair — the rail then renders the label alone rather than a broken image.
 *  - available  whether the entry is built and enabled. Gates BOTH the rail and, transitively, the
 *               avatar-menu item (§4 decision 4). An entry may only be true once its pane exists:
 *               a true entry backed by `NotBuiltYet` renders a blank pane, and because the rail is
 *               ordered by SETTINGS_GROUP_ORDER, an unbuilt ACCOUNT entry would be what the modal
 *               OPENS on. Members shipped in P1.11 (ROAR-4802, ROAR-4969); Audit log stays false
 *               until Phase 2. A BUILT pane can also be false — see `profile`: having the component
 *               is not enough when the endpoint behind it does not exist yet.
 *  - Component  the pane. Receives `onNavigate(path)`, which closes the modal BEFORE navigating
 *               (§4 decision 2) — panes must never call `navigate()` directly.
 *  - featureKey optional `user.features` key (ROAR-5007). When the company's plan marks that feature
 *               `hide: true` the entry is dropped from the rail (and, its group emptied, the group
 *               heading with it — Members is COMPANY's only entry). Only an explicit `hide: true`
 *               hides; a missing map or key shows the entry. This is a plan gate, not a role gate,
 *               so it does not contradict the "no `roles` field" rule below.
 *
 * Deliberately NO per-entry `roles` field: gating lives at the avatar-menu item (§4 decision 6)
 * and at the action controls inside a pane (§4 decision 10). A third, registry-level gate would be
 * a divergent mechanism. If P1.11 needs per-pane roles, add the field AND the rail filter together.
 */

/** Rail heading order. Entries render grouped in exactly this sequence, per the Figma rail. */
export const SETTINGS_GROUP_ORDER = ['ACCOUNT', 'BILLING', 'COMPANY', 'SECURE'];

/** Placeholder for entries whose pane has not been built yet; never rendered while available:false. */
const NotBuiltYet = () => null;

export const SETTINGS_REGISTRY = {
  profile: {
    key: 'profile',
    label: 'Profile',
    group: 'ACCOUNT',
    iconName: 'Profile',
    /**
     * STILL FALSE after ROAR-4963 — but for a narrower reason than before.
     *
     * ROAR-4945 set this false because `PATCH /account/profile` did not exist at all. It now does
     * (ROAR-4964), and ROAR-4963 has reconciled the payload against the allow-list that shipped.
     * What is missing is only the MERGE: ROAR-4964 sits on `api-backend-provapt`
     * `feature/ROAR-4964`, not on `feature/ROAR-4899`, so the Story branch still has no profile
     * handler and a Save would 404.
     *
     * Flip to true once ROAR-4964 is merged and the pane has been exercised against a backend
     * carrying it. That is the ONLY remaining step — no FE change is outstanding.
     */
    available: true,
    Component: Profile,
  },
  credits_usage: {
    key: 'credits_usage',
    label: 'Credits & Usage',
    group: 'BILLING',
    iconName: 'Credit',
    available: true, // ROAR-4945: reconciled against the ratified contract and enabled
    Component: CreditsUsage,
  },
  payments_invoices: {
    key: 'payments_invoices',
    label: 'Payments & Invoices',
    group: 'BILLING',
    iconName: 'Card',
    available: true, // ROAR-4945: reconciled against the ratified contract and enabled
    featureKey: FEATURE_KEYS.BILLING,
    Component: PaymentsInvoices,
  },
  members: {
    key: 'members',
    label: 'Members',
    group: 'COMPANY',
    iconName: 'Members', // public/images/MembersActive.svg + MembersInactive.svg
    available: true,
    featureKey: FEATURE_KEYS.USER_INVITE,
    Component: Members,
  },
  login_activity: {
    key: 'login_activity',
    label: 'Login Activity',
    group: 'SECURE',
    iconName: 'LoginActivity',
    available: true, // ROAR-4945: reconciled against the ratified contract and enabled
    Component: LoginActivity,
  },
  audit_log: {
    key: 'audit_log',
    label: 'Audit log',
    group: 'SECURE',
    iconName: null, // no asset pair yet; ships with the pane in Phase 2
    available: false, // Phase 2 — no pane yet, so it must stay hidden
    Component: NotBuiltYet,
  },
};

/**
 * Available entries, ordered by SETTINGS_GROUP_ORDER then by registry order within each group.
 * Entries whose `featureKey` the plan marks `hide: true` are left out (ROAR-5007).
 *
 * @param {object} [registry] Registry to read; defaults to SETTINGS_REGISTRY.
 * @param {object|null|undefined} [features] `user.features` from validateLogin.
 * @returns {Array<object>} Available entries in rail order.
 */
export const getAvailableSettingsEntries = (registry = SETTINGS_REGISTRY, features) => {
  const available = Object.values(registry).filter(
    (entry) => entry.available && !(entry.featureKey && isFeatureHidden(features, entry.featureKey))
  );
  return SETTINGS_GROUP_ORDER.flatMap((group) =>
    available.filter((entry) => entry.group === group)
  );
};

/**
 * Filters entries by the "Search settings" term, matching a case-insensitive substring of `label`.
 * An empty or whitespace-only term matches everything.
 *
 * @param {Array<object>} entries Entries to filter.
 * @param {string} [term] Raw search input.
 * @returns {Array<object>} Matching entries, order preserved.
 */
export const searchSettingsEntries = (entries, term) => {
  const needle = (term || '').trim().toLowerCase();
  if (!needle) return entries;
  return entries.filter((entry) => entry.label.toLowerCase().includes(needle));
};

/**
 * Whether anything is available to show. The avatar-menu Settings item renders only when this is
 * true (§4 decision 4) — which is what keeps PRs 2-5 inert in production.
 *
 * @param {object} [registry] Registry to read; defaults to SETTINGS_REGISTRY.
 * @returns {boolean}
 */
export const hasAvailableSettingsEntry = (registry = SETTINGS_REGISTRY) =>
  Object.values(registry).some((entry) => entry.available);
